import React from "react";
import { Info } from "@phosphor-icons/react";
import { Tooltip } from "flowbite-react";
import { useAuth } from "../hooks";
import { formatNumber } from "../lib/utils";

const PLATFORM_LABELS: Record<string, string> = {
  google_ads: "Google Ads",
  apple_ads: "Apple Ads",
  meta_ads: "Meta Ads",
};

// Maps spend_breakdown keys to the platform strings used in integratedPlatforms
const PLATFORM_KEY_MAP: Record<string, string[]> = {
  google_ads: ["google_ads"],
  apple_ads: ["apple_ads", "apple_search_ads"],
  meta_ads: ["meta_ads", "facebook"],
};

interface ISpendBreakdownEntry {
  with_tax: number;
  without_tax: number;
  total: number;
  total_inr: number;
  with_tax_inr: number;
  without_tax_inr: number;
}

interface SpendBreakdownCellProps {
  value: number;
  breakdown?: Partial<Record<"google_ads" | "apple_ads" | "meta_ads", ISpendBreakdownEntry>>;
  taxType?: "without_tax" | "with_tax";
}

const SpendBreakdownCell: React.FC<SpendBreakdownCellProps> = ({
  value,
  breakdown,
  taxType = "without_tax",
}) => {
  const { connectedAdsPlatforms } = useAuth();
  const displayValue = isNaN(value) ? "0.00" : formatNumber(value);
  const title = taxType === "with_tax" ? "Spend Breakdown (with tax)" : "Spend Breakdown";

  const entries = breakdown
    ? Object.entries(breakdown)
        .filter(([key]) =>
          connectedAdsPlatforms.length === 0 ||
          (PLATFORM_KEY_MAP[key] ?? [key]).some((p) => connectedAdsPlatforms.includes(p))
        )
        .map(([key, v]) => ({ label: PLATFORM_LABELS[key] ?? key, amount: v?.[taxType] ?? 0 }))
    : [];

  if (entries.length === 0) {
    return (
      <div className="inline-flex items-center justify-end gap-1 tabular-nums w-full">
        <span className="text-sm whitespace-nowrap text-right block">{displayValue}</span>
        <span className="inline-flex w-[14px] justify-end invisible">
          <Info size={14} />
        </span>
      </div>
    );
  }

  return (
    <div className="w-full flex items-center justify-end relative">
      <Tooltip
        arrow={true}
        placement="auto"
        content={
          <div className="flex flex-col gap-2 min-w-44 text-left">
            <span className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">
              {title}
            </span>
            <div className="flex flex-col gap-1 max-h-44 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/20 hover:[&::-webkit-scrollbar-thumb]:bg-white/40">
              {entries.map((e) => (
                <div key={e.label} className="flex items-center justify-between gap-6">
                  <span className="text-xs text-gray-300">{e.label}:</span>
                  <span className={`text-xs tabular-nums font-semibold ${e.amount > 0 ? "text-blue-300" : "text-gray-500"}`}>
                    {formatNumber(e.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        }
      >
        <div className="inline-flex items-center justify-end gap-1 tabular-nums w-full">
          <span className="text-sm whitespace-nowrap text-right block">{displayValue}</span>
          <span className="inline-flex w-[14px] justify-end">
            <Info size={14} className="text-gray-400 hover:text-gray-600" />
          </span>
        </div>
      </Tooltip>
    </div>
  );
};

export default SpendBreakdownCell;
